// The Library: the account's projects on the web, last opened first, grouped by series, with word counts from the
// core. Each project is brought up to date in the background (so it also reads offline later) and counted once.
//
// An empty Library offers the sample project (and the foot of the page always does, as the Mac's Help ▸ Open Sample
// Project): store/sample.ts adds it under its own id, or opens it when the relay has it already.
//
// W8: each project's menu exports it as a `.writingsuite` package (the foot links to Export everything); a free
// account's usage shows against its limits, with a gentle note at 80%; and a writer who has just joined is welcomed
// (the join page leaves `ws:welcome` in localStorage) with the sample and Help.
import { useEffect, useRef, useState } from 'react';
import { greeting } from '../../../core/src/home/index.ts';
import { LiveProject } from '../store/project.ts';
import { projectSeries, projectTitle, type LibraryProject } from '../store/library.ts';
import { isOnline } from '../store/account.ts';
import { fetchSamplePackage, openSample } from '../store/sample.ts';
import { announce, useApp } from '../shell/context.ts';
import { projectRowDetail } from '../shell/Columns.tsx';
import { Masthead } from '../shell/Gate.tsx';
import { followLink, formatRoute, navigate, projectPath } from '../shell/route.ts';
import { useAccountName } from './Home.tsx';
import { seriesGroups } from './helpers.ts';
import { NewProjectDialog } from './plan/NewProject.tsx';
import { dismissOpenedFile, useOpenedFile } from '../store/openedFiles.ts';
import { MenuButton } from './Menu.tsx';
import { ExportStatus, useExport, type ExportRun } from './ExportPage.tsx';
import { ShareDialog } from './ShareDialog.tsx';
import { LeaveDialog, PendingShares, SharedBadge } from './SharedProject.tsx';
import { nearlyFullNote, usageRows, type Limits, type Usage } from '@writing-suite/client/browser/usage.ts';

const WELCOME_KEY = 'ws:welcome';

function readWelcome(): boolean {
  try {
    return localStorage.getItem(WELCOME_KEY) === '1';
  } catch {
    return false;
  }
}

/** The account's usage and limits (relays from W8 on), read once with the page. */
function useUsage(): { usage: Usage; limits: Limits } | null {
  const app = useApp();
  const [u, setU] = useState<{ usage: Usage; limits: Limits } | null>(null);
  useEffect(() => {
    if (!app.online) return;
    let live = true;
    void app.account.session
      .account()
      .then((a) => live && a.usage && setU({ usage: a.usage, limits: a.limits ?? null }))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [app.account, app.online]);
  return u;
}

/** A free account's usage against its limits (nothing for the owner, who has none), and the note at 80%. */
export function UsageLine({ usage, limits }: { usage: Usage; limits: Limits }) {
  if (!limits) return null;
  const rows = usageRows(usage, limits).filter((r) => r.limit !== null);
  const note = nearlyFullNote(usage, limits);
  return (
    <section className="library-usage" aria-label="Your account’s usage" data-usage>
      <ul>
        {rows.map((r) => (
          <li key={r.kind} data-usage-kind={r.kind} data-nearly-full={r.nearlyFull || undefined}>
            <span className="usage-label">{r.label}</span> <span className="usage-text">{r.text}</span>
            <span className="usage-meter" aria-hidden="true" ref={(el) => el?.style.setProperty('--used', `${Math.round(r.fraction * 100)}%`)} />
          </li>
        ))}
      </ul>
      {note ? (
        <p className="usage-note" role="status" data-usage-note>
          {note}{' '}
          <a href="/export" onClick={(e) => followLink(e, '/export')}>
            Export projects…
          </a>
        </p>
      ) : null}
    </section>
  );
}

function Welcome({ onDone }: { onDone: () => void }) {
  return (
    <section className="welcome" aria-labelledby="welcome-title" data-welcome>
      <h2 id="welcome-title">Welcome to Five Rooms</h2>
      <p>
        Everything you write here is encrypted in this browser before it’s saved, so only your devices can read it. Start with the sample project to
        see how a book can be set up, or make a project of your own with New Project.
      </p>
      <p>
        <a href="/help" onClick={(e) => followLink(e, '/help')}>
          Help
        </a>{' '}
        explains each room. Your account page has your passkeys, your recovery key and your usage.
      </p>
      <p className="actions">
        <button type="button" className="button secondary" onClick={onDone}>
          Got it
        </button>
      </p>
    </section>
  );
}

/** Projects brought up to date in this page's life (the Library doesn't do it again on every visit). */
const refreshed = new Set<string>();

export function sortProjects(list: LibraryProject[]): LibraryProject[] {
  const modified = (p: LibraryProject) => Date.parse(p.summary?.modifiedAt ?? '') || 0;
  return [...list].sort(
    (a, b) => (b.info.lastOpened ?? 0) - (a.info.lastOpened ?? 0) || modified(b) - modified(a) || b.createdAt - a.createdAt || projectTitle(a).localeCompare(projectTitle(b)),
  );
}

function useBackgroundRefresh(projects: LibraryProject[] | null) {
  const app = useApp();
  const running = useRef<LiveProject | null>(null);
  useEffect(() => {
    if (!projects) return;
    let stopped = false;
    void (async () => {
      for (const p of projects) {
        if (stopped) return;
        const online = isOnline();
        if (refreshed.has(p.id) && (p.info.words !== undefined || !online)) continue;
        if (!online && p.info.words !== undefined) continue;
        const live = new LiveProject(p.id, p.key, { api: app.account.session.api, store: app.account.store, online: isOnline, sessionEnded: app.sessionEnded }, false);
        running.current = live;
        try {
          await live.open();
          if (online) refreshed.add(p.id);
        } catch {
          // counted another time
        } finally {
          live.close();
          running.current = null;
        }
        if (!stopped) app.noteProject(p.id);
      }
    })();
    return () => {
      stopped = true;
      running.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects?.map((p) => p.id).join(','), app.online]);
}

/** Opens the sample project (adding it when the relay hasn't got it), then its Start Here note. */
function useSample() {
  const app = useApp();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const docs = await fetchSamplePackage();
      if (!docs) throw new Error('This build of the app has no sample project.');
      const opened = await openSample(docs, { api: app.account.session.api, accountId: app.account.accountId, avk: app.account.avk });
      await app.refreshProjects();
      announce(opened.added ? `Added “${opened.title}”` : `Opening “${opened.title}”`);
      // Start Here is a note in Research & Notes: it opens in Research.
      const room = opened.start?.research ? 'research' : 'write';
      navigate(
        formatRoute({
          name: 'project', projectId: opened.projectId, room, page: room,
          nodeId: opened.start?.nodeId ?? null, arrangementId: opened.start?.arrangementId ?? null, params: {},
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };
  return { busy, error, run };
}

export function Library({ missing }: { missing?: string }) {
  const app = useApp();
  const name = useAccountName();
  const sample = useSample();
  const heading = useRef<HTMLHeadingElement>(null);
  useBackgroundRefresh(app.projects);
  useEffect(() => {
    document.title = 'Library — Five Rooms';
    heading.current?.focus();
  }, []);

  const list = app.projects ? sortProjects(app.projects) : null;
  const [creating, setCreating] = useState(false);
  // A file the writer opened with Five Rooms for Windows: a Scrivener project or a Word document to import, or a note
  // (store/openedFiles.ts).
  const opened = useOpenedFile();
  const usage = useUsage();
  const exporting = useExport();
  const [welcome, setWelcome] = useState(readWelcome);
  const doneWelcome = () => {
    setWelcome(false);
    try {
      localStorage.removeItem(WELCOME_KEY);
    } catch {
      // this page only
    }
  };
  const groups = list ? seriesGroups(list, projectSeries) : [];
  const [sharing, setSharing] = useState<LibraryProject | null>(null);
  const [leavingProject, setLeavingProject] = useState<LibraryProject | null>(null);
  const offline = !app.online || app.listFromCache;

  return (
    <div className="library-page">
      <Masthead signedIn />
      <main className="library" data-state="library" aria-busy={list === null}>
        <h1 ref={heading} tabIndex={-1}>
          Library
        </h1>
        <p className="library-greeting">{greeting(name)}</p>
        {offline ? null : (
          <p className="library-actions">
            <button type="button" className="button secondary" onClick={() => setCreating(true)} data-new-project>
              New Project…
            </button>
          </p>
        )}
        {creating ? <NewProjectDialog author={name} onClose={() => setCreating(false)} /> : null}
        {(opened?.kind === 'scrivener' || opened?.kind === 'word') && !creating && !offline ? (
          <NewProjectDialog key={opened.key} author={name} opened={opened.kind === 'scrivener' ? { kind: 'scrivener', pkg: opened.pkg } : { kind: 'word', file: opened.file }}
            onClose={() => dismissOpenedFile(opened.key)} />
        ) : null}
        {opened && (opened.kind === 'note' || offline) ? (
          <p className="notice" role="status" data-opened-file>
            {opened.kind === 'note' ? opened.text : `“${opened.name}” is ready to import once you’re online.`}{' '}
            <button type="button" className="link-button" onClick={() => dismissOpenedFile(opened.key)}>
              Dismiss
            </button>
          </p>
        ) : null}
        {welcome ? <Welcome onDone={doneWelcome} /> : null}
        {usage && !offline ? <UsageLine usage={usage.usage} limits={usage.limits} /> : null}
        {offline ? (
          <p className="status-note" role="status" data-offline>
            Offline — showing what this computer has.
          </p>
        ) : null}
        {missing ? <p className="notice">There’s nothing at {missing}. Here are your projects.</p> : null}
        {list === null ? (
          <p className="lede">Opening your projects…</p>
        ) : list.length === 0 ? (
          <>
            <p className="lede">
              {offline
                ? 'This computer hasn’t kept any projects yet.'
                : 'No projects on the web yet. On your Mac, choose a project’s On the web setting to read it here.'}
            </p>
            {offline ? null : (
              <section className="sample-offer" aria-labelledby="sample-offer" data-sample-offer>
                <h2 id="sample-offer">Try the sample project</h2>
                <p>
                  Stevenson’s <em>Strange Case of Dr Jekyll and Mr Hyde</em>, set up the way you might set up a book of your own: synopses, story times,
                  characters and places, notes in the margin and checkpoints.
                </p>
                <p className="actions">
                  <button type="button" className="button primary" onClick={() => void sample.run()} disabled={sample.busy}>
                    {sample.busy ? 'Opening the sample…' : 'Open the sample project'}
                  </button>
                </p>
              </section>
            )}
          </>
        ) : (
          groups.map((g) => (
            <section key={g.series ?? ''} className="series" aria-labelledby={g.series ? `series-${g.series}` : undefined}>
              {g.series ? (
                <h2 className="eyebrow-heading" id={`series-${g.series}`}>
                  <span className="eyebrow">Series</span> {g.series}
                </h2>
              ) : groups.length > 1 ? (
                <h2 className="eyebrow-heading">
                  <span className="eyebrow">Other projects</span>
                </h2>
              ) : null}
              <ul className="project-list">
                {g.items.map((p) => (
                  <ProjectRow key={p.id} project={p} online={app.online} exporting={exporting} onShare={setSharing} onLeave={setLeavingProject} />
                ))}
              </ul>
            </section>
          ))
        )}
        <PendingShares />
        <ExportStatus run={exporting} />
        {sharing ? <ShareDialog project={sharing} live={null} onClose={() => setSharing(null)} /> : null}
        {leavingProject ? <LeaveDialog project={leavingProject} onClose={() => setLeavingProject(null)} /> : null}
        {sample.error ? (
          <p className="error" role="alert">
            {sample.error}
          </p>
        ) : null}
        {list && list.length > 0 ? (
          <p className="library-foot">
            <button type="button" className="link-button" onClick={() => void sample.run()} disabled={sample.busy || offline} data-open-sample>
              {sample.busy ? 'Opening the sample…' : 'Open the sample project'}
            </button>
            <a href="/export" onClick={(e) => followLink(e, '/export')} data-export-link>
              Export everything…
            </a>
            <a href="/help" onClick={(e) => followLink(e, '/help')}>
              Help
            </a>
            <a href="/privacy">Privacy</a>
          </p>
        ) : (
          <p className="library-foot">
            <a href="/help" onClick={(e) => followLink(e, '/help')}>
              Help
            </a>
            <a href="/privacy">Privacy</a>
          </p>
        )}
      </main>
    </div>
  );
}

function ProjectRow({ project, online, exporting, onShare, onLeave }: {
  project: LibraryProject; online: boolean; exporting: ExportRun; onShare: (p: LibraryProject) => void; onLeave: (p: LibraryProject) => void;
}) {
  const href = `${projectPath(project.id)}/write`;
  const words = project.info.words;
  const detail = projectRowDetail(project);
  const cut = detail.indexOf(' · ');
  const kind = cut < 0 ? detail : detail.slice(0, cut);
  const rest = cut < 0 ? '' : detail.slice(cut + 3);
  return (
    <li className="project-row" data-project-id={project.id}>
      <a href={href} onClick={(e) => followLink(e, href)}>
        <span className="project-title">{projectTitle(project)}</span>
        <SharedBadge project={project} />
        <span className="project-meta">
          {kind} ·{' '}
          <span data-words={words ?? ''} className={words === undefined ? 'pending' : undefined}>
            {words !== undefined ? rest : online ? 'counting…' : 'not on this computer yet'}
          </span>
        </span>
      </a>
      <MenuButton
        label={`${projectTitle(project)}: more`}
        className="quiet-button project-row-menu"
        menuLabel={projectTitle(project)}
        disabled={exporting.busy}
        entries={() => [
          { label: 'Export…', onSelect: () => void exporting.run([project]) },
          project.shared
            ? { label: 'Leave Project…', disabled: !online && project.shared.status === 'active', onSelect: () => onLeave(project) }
            : { label: 'Share Project…', disabled: !online, onSelect: () => onShare(project) },
        ]}
      >
        ⋯
      </MenuButton>
    </li>
  );
}
